import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from 'convex/react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Icons } from '@/components/ui/icons';
import { useEffect, useRef } from 'react';
import { trackPaymentCompleted } from '@/lib/analytics';

const formatAmount = (amount: number, currency: string): string => {
  try {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: (currency || 'eur').toUpperCase(),
    }).format(amount / 100);
  } catch {
    return `${(amount / 100).toFixed(2)} ${(currency || 'EUR').toUpperCase()}`;
  }
};

const SuccessPage = () => {
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get('session_id') ?? '';
  const data = useQuery(api.app.billing.getPaymentBySession, sessionId ? { sessionId } : 'skip');
  const trackedPaymentRef = useRef<string | null>(null);

  const succeeded = data?.status === 'succeeded';

  useEffect(() => {
    if (!succeeded || !data || trackedPaymentRef.current === sessionId) {
      return;
    }

    trackedPaymentRef.current = sessionId;
    trackPaymentCompleted('provider_platform_fee', {
      payment_id: data.molliePaymentId,
      amount: data.amount / 100,
      currency: data.currency || 'EUR',
      source: 'billing',
      mode: 'checkout',
    });
  }, [data, sessionId, succeeded]);

  const backToService = (label: string, variant: 'default' | 'outline') => (
    <Button variant={variant} className='w-full' render={data?.serviceId ? <Link to='/provider/service/$id' params={{ id: data.serviceId }} /> : <Link to='/provider/services' />}>
      {label}
      <Icons.ArrowRight className='ml-2 size-4' />
    </Button>
  );

  return (
    <div className='mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center px-4 py-12'>
      <Card className='w-full'>
        <CardContent className='flex flex-col items-center gap-5 p-8 text-center'>
          {data === undefined ? (
            <>
              <div className='flex size-14 items-center justify-center rounded-full bg-muted'>
                <Icons.Clock className='size-7 animate-pulse text-muted-foreground' />
              </div>
              <div className='space-y-2'>
                <h1 className='text-xl font-semibold text-foreground'>Zahlung wird verarbeitet</h1>
                <p className='text-sm leading-relaxed text-muted-foreground'>Bitte haben Sie einen Moment Geduld, während wir Ihre Zahlung bestätigen.</p>
              </div>
            </>
          ) : succeeded ? (
            <>
              <div className='flex size-14 items-center justify-center rounded-full bg-success/10'>
                <Icons.CheckCircle className='size-7 text-success' />
              </div>
              <div className='space-y-2'>
                <h1 className='text-xl font-semibold text-foreground'>Zahlung erfolgreich</h1>
                <p className='text-sm leading-relaxed text-muted-foreground'>
                  Vielen Dank. Ihre Zahlung über <span className='font-semibold text-foreground'>{formatAmount(data.amount, data.currency)}</span> ist eingegangen. Ihr Diensttauschformular wird erstellt und steht in den Dienstdetails zum Download bereit.
                </p>
              </div>
              <div className='w-full pt-2'>{backToService('Zum Dienst', 'default')}</div>
            </>
          ) : (
            <>
              <div className='flex size-14 items-center justify-center rounded-full bg-status-warning-light'>
                <Icons.Clock className='size-7 text-status-warning' />
              </div>
              <div className='space-y-2'>
                <h1 className='text-xl font-semibold text-foreground'>Zahlung wird noch bestätigt</h1>
                <p className='text-sm leading-relaxed text-muted-foreground'>Wir konnten Ihre Zahlung noch nicht abschließend bestätigen. Sobald sie bestätigt ist, sehen Sie den Status in den Dienstdetails. Dies kann einige Minuten dauern.</p>
              </div>
              <div className='w-full pt-2'>{backToService('Zu meinen Diensten', 'outline')}</div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const Route = createFileRoute('/provider/payment/success')({
  component: SuccessPage,
});
